/**
 * 속견표 페이지 — ⭐ Master-Detail 셸 (Phase 2).
 *   좌측: 영구 네비게이터(검색 + 국문/EN + "속견표 전체" + 부(部)→류 아코디언, 접기 가능)
 *   우측: 호 미선택 시 속견표 그리드(류→호 in-place 드릴), 호 선택 시 상세(HeadingDetailPage)
 *
 *   → 하위(상세)로 들어가도 좌측 속견표 네비가 항상 살아있어 언제든 전체로 복귀.
 *   URL: /?audit=quickref&qhs=8471&qch=84  (딥링크/뒤로가기 지원, audit 라우트는 quickref 유지)
 */
import React, { useState, useEffect } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Star,
  Clock,
} from "lucide-react";
import QuickReferenceGrid from "./QuickReferenceGrid";
import HeadingDetailPage from "./HeadingDetailPage";
import WatchlistView from "./WatchlistView";
import HsChangesView from "./HsChangesView";
import WatchStar from "./WatchStar";
import { useWatchlist } from "./watchlistApi";
import { SECTIONS, CHAPTERS_97, CHAPTERS_97_EN } from "./chaptersData";
import "./theme.css";

const NAME_BY_CH = Object.fromEntries(CHAPTERS_97.map((c) => [c.ch, c.name]));
const SEC_OF_CH = {};
SECTIONS.forEach((s) =>
  (s.chapters || []).forEach((ch) => (SEC_OF_CH[ch] = s.sec)),
);

function readSel() {
  if (typeof window === "undefined") return { hs: null, ch: null, view: null };
  const p = new URLSearchParams(window.location.search);
  return { hs: p.get("qhs"), ch: p.get("qch"), view: p.get("view") };
}
function syncUrl(hs, ch, view) {
  const p = new URLSearchParams(window.location.search);
  p.set("audit", "quickref");
  if (hs) p.set("qhs", hs);
  else p.delete("qhs");
  if (ch) p.set("qch", ch);
  else p.delete("qch");
  if (view) p.set("view", view); // 내품목/변경내역 뷰(관세율표 툴바·딥링크)
  else p.delete("view"); // 없으면 그리드
  p.delete("hsk"); // 호/류 전환 시 이전 세번(hsk) 선택 잔여 제거
  // 라우트(audit)는 quickref 유지 → App 패널 전환 없음. popstate 안 쏨(상태 직접 갱신).
  window.history.pushState({}, "", "/?" + p.toString());
}

export default function QuickRefPage() {
  const init = readSel();
  // 관세율표 진입(?audit=quickref&noextra=1) — 속견표 전용 3버튼(전체·내품목·변경내역) 숨김.
  //   그리드·좌측 아코디언은 그대로. 전체 복귀는 그리드 내부 "← 속견표" 브레드크럼이 담당.
  const hideExtras =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("noextra") === "1";
  const [q, setQ] = useState("");
  const [lang, setLang] = useState("Kor");
  const [subCh, setSubChState] = useState(init.ch);
  const [selHs, setSelHsState] = useState(init.hs);
  // 관세율표(noextra=1)는 좌측 'HS 탐색' 네비를 기본 접힘으로 열어 그리드가 바로 보이게
  //   한다(사용자 요청 2026-07-14). 속견표(일반 quickref)는 3버튼·검색이 있는 네비를 펼침 유지.
  const [navOpen, setNavOpen] = useState(
    () =>
      typeof window !== "undefined" && window.innerWidth >= 860 && !hideExtras,
  );
  const [openSec, setOpenSec] = useState(() =>
    init.ch ? SEC_OF_CH[init.ch] : null,
  );
  const [vw, setVw] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1440,
  );
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const isMobile = vw < 860;
  // 모바일은 기본 접힘(오버레이 드로어). 관세율표(hideExtras)는 데스크톱에서도 접힘.
  //   메뉴 전환(관세율표↔속견표)은 popstate→재렌더로 hideExtras 가 바뀌므로 여기서 반영되고,
  //   같은 화면 내 수동 토글은 deps 불변이라 유지된다.
  useEffect(() => {
    setNavOpen(!isMobile && !hideExtras);
  }, [isMobile, hideExtras]);

  // 뒤로/앞으로 + 딥링크 — URL ↔ 상태 동기화
  //   64차: 이 화면은 keep-alive 라 통관정보 등 다른 화면으로 간 뒤에도 popstate 를 받는다.
  //   그때 URL 에는 qhs/qch 가 없어 선택 호·세번을 지우고, 돌아오면 다시 만들어져 보던 자리를 잃었다.
  //   → 현재 라우트가 quickref 일 때만 URL 을 상태에 반영한다.
  useEffect(() => {
    const h = () => {
      if (new URLSearchParams(window.location.search).get("audit") !== "quickref") return;
      const s = readSel();
      setSubChState(s.ch);
      setSelHsState(s.hs);
      if (s.ch) setOpenSec(SEC_OF_CH[s.ch]);
      setWatchView(s.view === "watch");
      setChangesView(s.view === "changes");
    };
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);

  const [selHsName, setSelHsName] = useState(null); // 별/복귀바 표시용 호명
  const [watchView, setWatchView] = useState(init.view === "watch"); // "내 품목"(딥링크)
  const [changesView, setChangesView] = useState(init.view === "changes"); // "변경 내역"(딥링크)
  const wl = useWatchlist(); // 변동 배지(changeCount)용

  // 액션 — 한 곳에서 상태+URL 동시 갱신
  const selectHs = (hs4, name) => {
    setSelHsState(hs4);
    if (name !== undefined) setSelHsName(name);
    setWatchView(false);
    setChangesView(false);
    syncUrl(hs4, subCh);
    if (isMobile) setNavOpen(false);
  };
  const gotoChapter = (ch) => {
    setSubChState(ch);
    setSelHsState(null);
    setWatchView(false);
    setChangesView(false);
    if (ch) setOpenSec(SEC_OF_CH[ch]); // ch=null(루트 복귀) 시 펼친 부 유지
    syncUrl(null, ch);
    if (isMobile) setNavOpen(false);
  };
  const showAll = () => {
    setSubChState(null);
    setSelHsState(null);
    setWatchView(false);
    setChangesView(false);
    syncUrl(null, null);
    if (isMobile) setNavOpen(false);
  };
  const openWatch = () => {
    setWatchView(true);
    setChangesView(false);
    syncUrl(null, null, "watch");
    if (isMobile) setNavOpen(false);
  };
  const openChanges = () => {
    setChangesView(true);
    setWatchView(false);
    syncUrl(null, null, "changes");
    if (isMobile) setNavOpen(false);
  };

  // 재사용 조각 — 데스크톱 드로어(navInner)·모바일 루트 양쪽에서 사용
  const searchEl = (
    <div style={{ position: "relative" }}>
      <Search
        size={14}
        style={{
          position: "absolute",
          left: 9,
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--audit-text-faint)",
        }}
      />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="류/호명·번호 검색"
        style={navSearch}
      />
    </div>
  );
  const accordionEl = (
    <div>
      {SECTIONS.map((s, i) => {
        const open = openSec === s.sec;
        return (
          <div key={s.sec} style={{ marginBottom: 2 }}>
            <button
              onClick={() => setOpenSec(open ? null : s.sec)}
              style={secBtn}
            >
              <span style={{ flex: 1, textAlign: "left" }}>
                <b style={{ fontWeight: 700 }}>{s.sec}</b> {s.name}
              </span>
              {open ? (
                <ChevronLeft
                  size={13}
                  style={{ transform: "rotate(-90deg)" }}
                />
              ) : (
                <ChevronRight size={13} />
              )}
            </button>
            {open && (
              <div style={{ padding: "2px 0 4px 10px" }}>
                {s.chapters.map((ch) => (
                  <button
                    key={ch}
                    onClick={() => gotoChapter(ch)}
                    style={{
                      ...chBtn,
                      background:
                        subCh === ch && !selHs
                          ? "var(--audit-accent-bg)"
                          : "transparent",
                      fontWeight: subCh === ch && !selHs ? 700 : 500,
                    }}
                  >
                    <span style={chBtnNum}>{ch}</span>
                    <span style={chBtnName}>
                      {lang === "En"
                        ? CHAPTERS_97_EN[ch] || NAME_BY_CH[ch]
                        : NAME_BY_CH[ch]}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const navInner = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={navHeader}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>HS 탐색</span>
        <button
          onClick={() => setNavOpen(false)}
          style={iconBtn}
          title="네비게이터 접기"
        >
          <ChevronLeft size={16} />
        </button>
      </div>
      <div
        style={{
          padding: "10px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {!hideExtras && searchEl}
        {!hideExtras && (
        <>
        <button
          onClick={showAll}
          style={{
            ...allBtn,
            background:
              !subCh && !selHs && !watchView
                ? "var(--audit-accent)"
                : "var(--audit-bg-subtle)",
            color:
              !subCh && !selHs && !watchView ? "#fff" : "var(--audit-text)",
            borderColor:
              !subCh && !selHs && !watchView
                ? "var(--audit-accent)"
                : "var(--audit-border)",
          }}
        >
          <LayoutGrid size={14} /> 속견표 전체
        </button>
        <button
          onClick={openWatch}
          style={{
            ...allBtn,
            background: watchView
              ? "var(--audit-accent)"
              : "var(--audit-bg-subtle)",
            color: watchView ? "#fff" : "var(--audit-text)",
            borderColor: watchView
              ? "var(--audit-accent)"
              : "var(--audit-border)",
          }}
        >
          <Star size={14} /> 세번 즐겨찾기
          {wl.changeCount > 0 && <span style={navBadge}>{wl.changeCount}</span>}
        </button>
        <button
          onClick={openChanges}
          style={{
            ...allBtn,
            background: changesView
              ? "var(--audit-accent)"
              : "var(--audit-bg-subtle)",
            color: changesView ? "#fff" : "var(--audit-text)",
            borderColor: changesView
              ? "var(--audit-accent)"
              : "var(--audit-border)",
          }}
        >
          <Clock size={14} /> 변경 내역
        </button>
        </>
        )}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 8px 12px" }}>
        {accordionEl}
      </div>
    </div>
  );

  // ───────── 모바일: 세로 드릴(부→류 아코디언 → 호 리스트 → 상세) ─────────
  if (isMobile) {
    return (
      <div
        className="audit-root"
        style={{
          height: "100%",
          overflowY: "auto",
          background: "var(--audit-bg)",
        }}
      >
        {changesView ? (
          <>
            <div style={mHeaderBar}>
              <button onClick={showAll} style={mBackBtnInline}>
                <ChevronLeft size={16} /> 속견표
              </button>
              <span style={{ fontWeight: 700, fontSize: 14 }}>변경 내역</span>
            </div>
            <div style={{ padding: "10px 12px 24px" }}>
              <HsChangesView onOpen={(code) => selectHs(code.slice(0, 4))} />
            </div>
          </>
        ) : watchView ? (
          <>
            <div style={mHeaderBar}>
              <button onClick={showAll} style={mBackBtnInline}>
                <ChevronLeft size={16} /> 속견표
              </button>
              <span style={{ fontWeight: 700, fontSize: 14 }}>세번 즐겨찾기</span>
            </div>
            <div style={{ padding: "10px 12px 24px" }}>
              <WatchlistView onOpen={(code) => selectHs(code.slice(0, 4))} />
            </div>
          </>
        ) : selHs ? (
          <>
            <div style={mHeaderBar}>
              <button
                onClick={() => {
                  setSelHsState(null);
                  syncUrl(null, subCh);
                }}
                style={mBackBtnInline}
              >
                <ChevronLeft size={16} />{" "}
                {subCh ? `제${parseInt(subCh, 10)}류 호 목록` : "속견표"}
              </button>
              <WatchStar
                code={selHs}
                name={selHsName}
                label={false}
                size={16}
              />
            </div>
            <HeadingDetailPage
              key={selHs}
              embedded
              compact
              hsOverride={selHs}
              onHsSelect={selectHs}
            />
          </>
        ) : subCh ? (
          <div style={{ padding: "10px 12px 24px" }}>
            <QuickReferenceGrid
              isMobile
              q={q}
              onQChange={setQ}
              lang={lang}
              onLangChange={setLang}
              subCh={subCh}
              onSubChChange={(ch) => gotoChapter(ch)}
              onHeadingClick={(hs4, ch, name) => selectHs(hs4, name)}
              hideControls
            />
          </div>
        ) : (
          <div style={{ padding: "10px 12px 28px" }}>
            <div
              style={{ fontSize: 16, fontWeight: 700, margin: "4px 2px 10px" }}
            >
              속견표 · HS 탐색
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginBottom: 12,
              }}
            >
              {!hideExtras && searchEl}
              {!hideExtras && (
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={openWatch} style={mWatchBtn}>
                    <Star size={15} /> 세번 즐겨찾기
                    {wl.changeCount > 0 && (
                      <span style={navBadge}>{wl.changeCount}</span>
                    )}
                  </button>
                  <button onClick={openChanges} style={mWatchBtn}>
                    <Clock size={15} /> 변경 내역
                  </button>
                </div>
              )}
            </div>
            <p
              style={{
                fontSize: 12.5,
                color: "var(--audit-text-muted)",
                margin: "0 0 10px",
              }}
            >
              부(部)를 펼쳐 류를 고르면 그 류의 호 목록 → 소호·세번 상세로
              이동합니다.
            </p>
            {accordionEl}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="audit-root"
      style={{
        height: "100%",
        display: "flex",
        background: "var(--audit-bg)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ── 좌측 네비게이터 ── */}
      {navOpen ? (
        isMobile ? (
          <>
            <div
              onClick={() => setNavOpen(false)}
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 40,
              }}
            />
            <div
              style={{
                ...navCol,
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                zIndex: 41,
                boxShadow: "2px 0 18px rgba(0,0,0,0.22)",
              }}
            >
              {navInner}
            </div>
          </>
        ) : (
          <div style={navCol}>{navInner}</div>
        )
      ) : (
        <div style={navRail}>
          <button
            onClick={() => setNavOpen(true)}
            style={iconBtn}
            title="HS 탐색 펼치기"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ── 우측 메인 ── */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* 관세율표 전용 툴바 — 속견표 메뉴 은퇴로 내품목·변경내역을 여기로(사용자 요청 2026-07-14).
            상세(selHs) 화면엔 자체 복귀바가 있어 미표시. */}
        {hideExtras && !selHs && (
          <div style={qrTabBar}>
            <button onClick={showAll} style={qrTab(!watchView && !changesView)}>
              <LayoutGrid size={14} /> 속견표 그리드
            </button>
            <button onClick={openWatch} style={qrTab(watchView)}>
              <Star size={14} /> 세번 즐겨찾기
              {wl.changeCount > 0 && <span style={navBadge}>{wl.changeCount}</span>}
            </button>
            <button onClick={openChanges} style={qrTab(changesView)}>
              <Clock size={14} /> 변경 내역
            </button>
          </div>
        )}
        {changesView ? (
          <div style={{ overflow: "auto", height: "100%" }}>
            <div style={{ padding: "18px 22px 28px", maxWidth: 860 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>
                변경 내역
              </h2>
              <p style={introText}>
                기준일 이후 적용개시(strt)된 세번을 시계열로 보여줍니다(관세청
                적용기간 기준). 행 클릭 → 해당 호 상세.
              </p>
              <HsChangesView onOpen={(code) => selectHs(code.slice(0, 4))} />
            </div>
          </div>
        ) : watchView ? (
          <div style={{ overflow: "auto", height: "100%" }}>
            <div style={{ padding: "18px 22px 28px", maxWidth: 760 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px" }}>
                세번 즐겨찾기
              </h2>
              <p style={introText}>
                자주 다루는 호를 모아 빠르게 확인합니다. 상세 화면 상단
                <b> ★ 즐겨찾기 추가</b>로 등록하세요. 변동(파란 배지)은 등록
                품목의 적용개시일 변경입니다.
              </p>
              <WatchlistView onOpen={(code) => selectHs(code.slice(0, 4))} />
            </div>
          </div>
        ) : selHs ? (
          <>
            {/* 상세 → 속견표 복귀 바 (연결감) */}
            <div style={detailBackBar}>
              {subCh && (
                <button
                  onClick={() => {
                    setSelHsState(null);
                    syncUrl(null, subCh);
                  }}
                  style={backChip}
                >
                  <ChevronLeft size={15} /> 제{parseInt(subCh, 10)}류 호 목록
                </button>
              )}
              <button onClick={showAll} style={backChipGhost}>
                <LayoutGrid size={14} /> 속견표 전체
              </button>
              <WatchStar code={selHs} name={selHsName} />
              <span style={backBarHint}>
                ← 좌측 ‘HS 탐색’으로 다른 류도 바로 이동
              </span>
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <HeadingDetailPage
                key={selHs}
                embedded
                compact
                hsOverride={selHs}
                onHsSelect={selectHs}
              />
            </div>
          </>
        ) : (
          <div style={{ overflow: "auto", height: "100%" }}>
            <div style={{ padding: "14px 22px 28px" }}>
              <QuickReferenceGrid
                q={q}
                onQChange={setQ}
                lang={lang}
                onLangChange={setLang}
                subCh={subCh}
                onSubChChange={(ch) => gotoChapter(ch)}
                onHeadingClick={(hs4, ch, name) => selectHs(hs4, name)}
                hideControls
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const navCol = {
  width: 264,
  flexShrink: 0,
  borderRight: "1px solid var(--audit-border)",
  background: "var(--audit-bg-elevated)",
  height: "100%",
  overflow: "hidden",
};
const navRail = {
  width: 38,
  flexShrink: 0,
  borderRight: "1px solid var(--audit-border)",
  background: "var(--audit-bg-elevated)",
  display: "flex",
  justifyContent: "center",
  paddingTop: 10,
};
const navHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 12px",
  borderBottom: "1px solid var(--audit-border)",
  color: "var(--audit-text)",
};
// 데스크톱 상세 → 속견표 복귀 바
const detailBackBar = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexShrink: 0,
  padding: "8px 14px",
  borderBottom: "1px solid var(--audit-border)",
  background: "var(--audit-bg-elevated)",
};
const backChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "1px solid var(--audit-accent)",
  background: "var(--audit-accent)",
  color: "#fff",
  borderRadius: 7,
  padding: "5px 11px",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
const backChipGhost = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg)",
  color: "var(--audit-text)",
  borderRadius: 7,
  padding: "5px 11px",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
const backBarHint = {
  fontSize: 11.5,
  color: "var(--audit-text-faint)",
  marginLeft: "auto",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
// 모바일 상세/목록 상단 헤더 바 (복귀 링크 + 우측 액션)
const mHeaderBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  borderBottom: "1px solid var(--audit-border)",
  background: "var(--audit-bg-elevated)",
  padding: "9px 12px",
  position: "sticky",
  top: 0,
  zIndex: 5,
};
const mBackBtnInline = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "none",
  background: "transparent",
  color: "var(--audit-accent)",
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  padding: 0,
};
const mWatchBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  alignSelf: "flex-start",
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg-subtle)",
  color: "var(--audit-text)",
  borderRadius: 7,
  padding: "7px 12px",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
// 변동 건수 배지 ("내 품목" 버튼)
const navBadge = {
  marginLeft: 4,
  minWidth: 16,
  height: 16,
  padding: "0 4px",
  borderRadius: 8,
  background: "var(--audit-error, #d93025)",
  color: "#fff",
  fontSize: 10.5,
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const iconBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg)",
  borderRadius: 6,
  width: 26,
  height: 26,
  cursor: "pointer",
  color: "var(--audit-text-muted)",
  padding: 0,
};
const navSearch = {
  width: "100%",
  fontSize: 12.5,
  padding: "7px 10px 7px 28px",
  borderRadius: 7,
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg)",
  color: "var(--audit-text)",
  boxSizing: "border-box",
};
const allBtn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  justifyContent: "center",
  border: "1px solid var(--audit-border)",
  borderRadius: 7,
  padding: "7px 10px",
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
const secBtn = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  border: "none",
  background: "transparent",
  padding: "7px 6px",
  fontSize: 12,
  cursor: "pointer",
  color: "var(--audit-text)",
  fontFamily: "inherit",
  borderRadius: 6,
};
const chBtn = {
  display: "flex",
  alignItems: "baseline",
  gap: 7,
  width: "100%",
  border: "none",
  padding: "5px 8px",
  fontSize: 12,
  cursor: "pointer",
  color: "var(--audit-text)",
  fontFamily: "inherit",
  borderRadius: 5,
  textAlign: "left",
};
const chBtnNum = {
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 10.5,
  color: "var(--audit-text-faint)",
  flexShrink: 0,
  width: 18,
};
const chBtnName = {
  flex: 1,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const introText = {
  fontSize: 12.5,
  color: "var(--audit-text-muted)",
  margin: "0 0 14px",
};
// 관세율표 상단 툴바(그리드·내품목·변경내역) — 밑줄 탭 스타일
const qrTabBar = {
  display: "flex",
  gap: 4,
  flexShrink: 0,
  padding: "8px 20px 0",
  borderBottom: "1px solid var(--audit-border)",
  background: "var(--audit-bg)",
};
const qrTab = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  border: "none",
  borderBottom: active
    ? "2px solid var(--audit-accent)"
    : "2px solid transparent",
  background: "transparent",
  color: active ? "var(--audit-text)" : "var(--audit-text-muted)",
  fontWeight: active ? 700 : 500,
  fontSize: 13,
  padding: "8px 10px 10px",
  marginBottom: -1,
  cursor: "pointer",
  fontFamily: "inherit",
});
